<template>
  <div class="row justify-content-md-center mt-2">
    <div :class="mapContainerClass" id="svg-mapchart"></div>
    <div id="popupOL" class="ol-popup">
      <div id="popup-content"></div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import * as d3 from 'd3'
import Rainbow from 'rainbowvis.js'
import { utilsMixin } from '../../utils/mixins'
import geolocConfig from '../../static/geoloc.json'
import Map from 'ol/Map.js'
import View from 'ol/View.js'
import TileLayer from 'ol/layer/Tile.js'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Overlay from 'ol/Overlay'
import OSM from 'ol/source/OSM'
import { transform } from 'ol/proj.js'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style.js'
import GeoJSON from 'ol/format/GeoJSON'
import { toLonLat } from 'ol/proj'

import 'ol/ol.css'

export default {
  name: 'OpenStreetMap',
  mixins: [utilsMixin],
  dependencies: ['adminService', 'dataService'],
  props: ['dataset', 'routeForm', 'startDate', 'endDate'],
  data() {
    return {
      timeout: null,
      d3Map: null,
      canvasWidth: 1280,
      canvasHeight: 720,
      fullScreen: false,
      svg: null,
      colorRainbow: new Rainbow(),
      levelsSVG: [],
      map: null,
      vector: null,
      vectors: [],
      geoloc: {},
      style: {
        Point: new Style({
          image: new CircleStyle({
            fill: new Fill({
              color: 'rgba(186,188,189,0.6)',
            }),
            radius: 10,
            stroke: new Stroke({
              color: 'rgba(186,188,189,0.6)',
              width: 1,
            }),
          }),
        }),
        LineString: new Style({
          stroke: new Stroke({
            color: '#0000ff',
            width: 3,
          }),
        }),
        MultiLineString: new Style({
          stroke: new Stroke({
            color: '#0000ff',
            width: 3,
          }),
        }),
      },
      overlay: null,
      content: null,
      container: null,
      range: [],
    }
  },
  watch: {
    dataset: {
      handler: function() {
        //If page is loaded
        if (document.getElementById('popupOL')) {
          this.updateDataValues()
          this.displayMapData()
        }
      },
      immediate: true,
    },
    routeForm: {
      handler: function() {},
      immediate: true,
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)

    //this.colorRainbow.setSpectrum('#FFF858', '#FF6B6B')
    this.colorRainbow.setSpectrum('#E5FD04', '#F10D0D')

    this.updateDataValues()
    this.displayMapData()
  },
  computed: {
    mapContainerClass: function() {
      return this.fullScreen
        ? {}
        : {
            'pb-4': true,
            'col-sm-9': this.isLargeScreen || this.isVeryLargeScreen,
            'col-sm-11': !this.isVeryLargeScreen,
          }
    },
    mapSiteIDs: function() {
      return [this.routeForm.site]
    },
  },
  methods: {
    updateDataValues: function() {
      this.dataset.routes.forEach(area => {
        let value
        switch (this.routeForm.dataType) {
          case 'average_time':
          case 'max_time':
          case 'min_time':
          case 'duration':
            value = moment
              .utc(moment.duration(area.value, 'seconds').asMilliseconds())
              .format(area.value > 3600 ? 'hh:mm:ss' : 'mm:ss')
            break
          case 'count':
          case 'session_count':
          case 'crash':
            value = area.value
            break
          default:
            value = area.value
            break
        }

        area['display_value'] = value
      })
    },
    displayMapData: function() {
      let that = this

      setTimeout(() => {
        this.container = document.getElementById('popupOL')
        this.content = document.getElementById('popup-content')

        this.overlay = new Overlay({
          element: this.container,
          autoPan: true,
          autoPanAnimation: {
            duration: 250,
          },
        })

        let max = 0
        let min = 0

        this.dataset.routes.forEach(area => {
          if (max < area.value) max = area.value
          if (min > area.value) min = area.value
        })

        if (min == max) {
          max = min + 1
        }

        this.colorRainbow.setNumberRange(min, max)
        this.range = [min, max]

        this.map = null
        d3.select('#svg-mapchart')
          .selectAll('*')
          .remove()

        this.vectors = this.createVectors()

        //Create maps according to his layers to display (checkpoints and or visitor's itinerary)
        this.map = new Map({
          target: 'svg-mapchart',
          layers: [new TileLayer({ source: new OSM() }), ...this.vectors],
          overlays: [this.overlay],
          view: new View({
            center: transform([2.3324060440063477, 48.86403720372053], 'EPSG:4326', 'EPSG:3857'),
            zoom: 13,
          }),
        })

        //On click event use to display the overlay pop up
        this.map.on('pointermove', event => {
          if (this.map.hasFeatureAtPixel(event.pixel) === true) {
            let selectedFeature = this.map.getFeaturesAtPixel(event.pixel)[0]
            let properties = selectedFeature.getProperties()
            let coordinate = event.coordinate
            var hdms = toLonLat(coordinate)

            let foundArea = this.dataset.routes.find(route => {
              return route.area_id == properties.area_id
            })

            if (foundArea) {
              this.content.innerHTML = `<p>${
                this.$i18n.messages[this.$locale]['map_areas'][this.routeForm.site][
                  foundArea.area_id
                ]
              } :  <strong>${foundArea.display_value}</strong></p>`

              if (this.dataset.areas) {
                foundArea = this.dataset.areas.find(area => {
                  return area.area == properties.area_id
                })
                this.content.innerHTML += `<p>${that.$t('timeline.start')} : <strong>${moment(
                  foundArea.start_time,
                ).format('HH:mm:ss')}</strong><br/>${that.$t('timeline.end')} : <strong>${moment(
                  foundArea.end_time,
                ).format('HH:mm:ss')}</strong></p>`
              }
            } else {
              this.content.innerHTML = `<p>Coordinates : <strong>${hdms}</strong></p>`
            }

            this.overlay.setPosition(coordinate)
          } else {
            this.overlay.setPosition(undefined)
          }
        })
      }, 500)
    },
    createVectors: function() {
      let that = this
      let vectors = []
      let vector, geoJSONFeature

      //Add itinerary if exists (for example in the Session component )
      if (this.dataset.geojson) {
        vector = new VectorLayer({
          source: new VectorSource({
            format: new GeoJSON(),
          }),
          style: function(feature) {
            return that.style[feature.getGeometry().getType()]
          },
        })

        geoJSONFeature = new GeoJSON().readFeatures(this.dataset.geojson, {
          featureProjection: 'EPSG:3857',
        })

        vector.getSource().addFeatures(geoJSONFeature)
        vectors.push(vector)
      }

      //Add HistoPad site checkpoints
      vector = new VectorLayer({
        source: new VectorSource({
          format: new GeoJSON(),
        }),
        style: function(feature) {
          let properties = feature.getProperties()
          if (properties && properties.area_id) {
            return that.generatePointStyle(properties.area_id)
          } else {
            return that.style[feature.getGeometry().getType()]
          }
        },
      })

      geoJSONFeature = new GeoJSON().readFeatures(this.getGeoJSONFromSite(), {
        featureProjection: 'EPSG:3857',
      })

      vector.getSource().addFeatures(geoJSONFeature)

      vectors.push(vector)

      return vectors
    },
    getGeoJSONFromSite: function() {
      let geolocJSON = geolocConfig[this.routeForm.site]

      let GeoJSON = {
        type: 'FeatureCollection',
        features: [],
      }

      //Populate features according to checkpoints set in the hgps file
      geolocJSON.coordinates.forEach(coord => {
        GeoJSON.features.push({
          type: 'Feature',
          properties: {
            area_name: coord.name,
            area_id: coord.areaID,
          },
          geometry: {
            type: 'Point',
            coordinates: [coord.longitude, coord.latitude],
          },
        })
      })

      return GeoJSON
    },
    closeOverlayPopup: function() {
      this.overlay.setPosition(undefined)
      return false
    },
    generatePointStyle: function(areaID) {
      let foundArea = this.dataset.routes.find(route => {
        return route.area_id == areaID
      })

      //If area is found we adjust the colors and the radius of the circle according to his value
      if (foundArea) {
        let radius = (foundArea.value * 20) / this.range[1]
        if (radius < 10) {
          //Minus of ten
          radius = 10
        }
        let rgba = this.hexToRGB('#' + this.colorRainbow.colorAt(foundArea.value))
        return new Style({
          image: new CircleStyle({
            fill: new Fill({
              color: `rgba(${rgba.r},${rgba.g},${rgba.b},0.65)`,
            }),
            radius: radius,
            stroke: new Stroke({
              color: '#' + this.colorRainbow.colorAt(foundArea.value),
              width: 1,
            }),
          }),
        })
      } else {
        //Return the default value
        return this.style['Point']
      }
    },
    hexToRGB: function(hex) {
      let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null
    },
  },
}
</script>

<style scoped>
#svg-mapchart {
  min-height: 600px;
}

.ol-popup {
  position: absolute;
  background-color: #343a40;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #343a40;
  bottom: 12px;
  left: -50px;
  min-width: 220px;
  color: #feffe9;
  font-size: 13px;
}
.ol-popup:after,
.ol-popup:before {
  top: 100%;
  border: solid transparent;
  content: ' ';
  height: 0;
  width: 0;
  position: absolute;
  pointer-events: none;
}
.ol-popup:after {
  border-top-color: #343a40;
  border-width: 10px;
  left: 48px;
  margin-left: -10px;
}
.ol-popup:before {
  border-top-color: #343a40;
  border-width: 11px;
  left: 48px;
  margin-left: -11px;
}
.ol-popup-closer {
  text-decoration: none;
  position: absolute;
  top: 2px;
  right: 8px;
}
.ol-popup-closer:after {
  content: '✖';
}
</style>
